// The Shipping hub's side of shipment signing (Phases 2-4, 2026-10-28; jobq-api main.py "SHIPMENT SIGNING" and
// "SHIPMENT LOADS"). Staff routes only, all keyed (config.js's fetch wrapper adds the API key); the public signing page
// (features/signing/SignPortal.jsx, /portal/sign/{token}) is another module.
//
//   POST /packaging/plans/{plan_id}/sign_requests   start a signing: {kind, pin, pallet_ids, load_checklist,
//                                                    client_request_id} -> {request_id, url, token, short_code, qr_svg,
//                                                    expires_at, ttl_sec, started_by, pallets}
//   GET  /shipping/sign_requests/{id}                the operator screen polls it: {status, signed, document_url, ...}
//   POST /shipping/sign_requests/{id}/void           {pin, reason}: Cancel
//   POST /shipping/sign_requests/{id}/reissue        {pin}: New code (same shape as create)
//   POST /packaging/plans/{plan_id}/pickup-paper     {pin, pallet_ids, load_checklist}: picked up, signature on paper
//   GET  /shipping/vehicles · GET /shipping/loads?kind=&since= · GET /shipping/loads/{id}/manifest.pdf
//   POST /packaging/plans/{plan_id}/delivery_signing  {pin, pallet_ids?}: a delivery code after a plain ship
//
// Every call resolves {ok, status, body, message} and never throws; `message` is the sentence to show.
import { v4 as uuidv4 } from 'uuid';
import { detailToString } from '../../utils/apiError';
import { isSystemUser } from '../users/systemUsers';

/** A fresh id for one request: sent again unchanged on a retry, so the API never does the work twice. */
export function newRequestId() {
  try {
    if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch (_) { /* fall back */ }
  return uuidv4();
}

async function call(url, { method = 'GET', body } = {}) {
  try {
    const opts = { method };
    if (body !== undefined) {
      opts.headers = { 'Content-Type': 'application/json' };
      opts.body = JSON.stringify(body);
    }
    const res = await fetch(url, opts);
    let data = null;
    try { data = await res.json(); } catch (_) { data = null; }
    if (!res.ok) {
      const d = data && typeof data === 'object' ? data.detail : null;
      return { ok: false, status: res.status, body: data, message: detailToString(d) || `HTTP ${res.status}` };
    }
    return { ok: true, status: res.status, body: data, message: '' };
  } catch (_) {
    return { ok: false, status: 0, body: null, message: 'Could not reach JobQ. Check the connection and try again.' };
  }
}

export function createSignRequest(apiBase, planId, { kind, pin, palletIds, loadChecklist = null, clientRequestId }) {
  const body = { kind, pin, pallet_ids: palletIds, client_request_id: clientRequestId };
  if (Array.isArray(loadChecklist)) body.load_checklist = loadChecklist;
  return call(`${apiBase}/packaging/plans/${planId}/sign_requests`, { method: 'POST', body });
}

export function readSignRequest(apiBase, requestId) {
  return call(`${apiBase}/shipping/sign_requests/${requestId}`);
}

export function voidSignRequest(apiBase, requestId, { pin, reason }) {
  return call(`${apiBase}/shipping/sign_requests/${requestId}/void`, { method: 'POST', body: { pin, reason } });
}

export function reissueSignRequest(apiBase, requestId, { pin }) {
  return call(`${apiBase}/shipping/sign_requests/${requestId}/reissue`, { method: 'POST', body: { pin } });
}

export function pickupOnPaper(apiBase, planId, { pin, palletIds, loadChecklist = null }) {
  const body = { pin, pallet_ids: palletIds };
  if (Array.isArray(loadChecklist)) body.load_checklist = loadChecklist;
  return call(`${apiBase}/packaging/plans/${planId}/pickup-paper`, { method: 'POST', body });
}

/** GET /shipping/vehicles -> {ok, vehicles: [{vehicle_id, name, kind}]} (active ones, in their order). A list or
 *  {vehicles: [...]} both read; a failed read is an empty list (the pickers still offer "Other (type it)"). */
export async function loadVehicles(apiBase) {
  const r = await call(`${apiBase}/shipping/vehicles`);
  const raw = r.ok ? (Array.isArray(r.body) ? r.body : (r.body && Array.isArray(r.body.vehicles) ? r.body.vehicles : [])) : [];
  const vehicles = raw
    .filter((v) => v && v.vehicle_id != null && v.name && v.active !== false)
    .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || String(a.name).localeCompare(String(b.name)));
  return { ok: r.ok, vehicles, message: r.message };
}

/** GET /users -> the people who can drive a load: active, never a program's account (Auto-Ship System, IoT System:
 *  users/systemUsers.js), by name. A failed read is an empty list. */
export async function loadDrivers(apiBase) {
  const r = await call(`${apiBase}/users`);
  const list = r.ok && Array.isArray(r.body) ? r.body : [];
  return list
    .filter((u) => u && u.user_id != null && u.name && u.is_active !== false && !isSystemUser(u))
    .map((u) => ({ user_id: u.user_id, name: u.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** GET /shipping/loads?kind=&since= -> {ok, loads, message}. A list or {loads: [...]} both read. */
export async function listLoads(apiBase, { kind = null, since = null } = {}) {
  const q = new URLSearchParams();
  if (kind) q.set('kind', kind);
  if (since) q.set('since', since);
  const qs = q.toString();
  const r = await call(`${apiBase}/shipping/loads${qs ? `?${qs}` : ''}`);
  if (!r.ok) return { ok: false, loads: [], message: r.message, status: r.status };
  const raw = Array.isArray(r.body) ? r.body : (r.body && Array.isArray(r.body.loads) ? r.body.loads : []);
  return { ok: true, loads: raw.filter((l) => l && typeof l === 'object'), message: '' };
}

/** POST /packaging/plans/{plan_id}/delivery_signing {pin, pallet_ids?}: a delivery code for pallets that shipped
 *  without one (the pending one again when there is one: `existing`). -> {ok, status, body (the link view), message}. */
export async function startDeliverySigning(apiBase, planId, { pin, palletIds = null } = {}) {
  const body = { pin };
  if (Array.isArray(palletIds) && palletIds.length) body.pallet_ids = palletIds.map(Number);
  return call(`${apiBase}/packaging/plans/${planId}/delivery_signing`, { method: 'POST', body });
}

export const loadManifestPath = (loadId) => `/shipping/loads/${loadId}/manifest.pdf`;

/** A path from the API ("/shipping/loads/7/manifest.pdf") or a full link (a presigned document) -> a URL to open. */
export function absoluteUrl(apiBase, pathOrUrl) {
  if (!pathOrUrl) return null;
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  return `${apiBase}${s.startsWith('/') ? '' : '/'}${s}`;
}

/** The QR the API drew (an <svg> string) as an <img> source: never put the API's markup into the page itself. */
export function qrImageSrc(svg) {
  if (!svg || typeof svg !== 'string') return null;
  try {
    // UTF-8 bytes -> base64 (the svg may carry non-ASCII text)
    const bin = encodeURIComponent(svg).replace(/%([0-9A-F]{2})/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
    return `data:image/svg+xml;base64,${window.btoa(bin)}`;
  } catch (_) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }
}

/** The token in a signing link (https://jobqworks.com/sign/<token>), else null. */
export function tokenFromUrl(url) {
  const m = /\/sign\/([^/?#\s]+)/.exec(String(url || ''));
  return m ? decodeURIComponent(m[1]) : null;
}

/** Seconds left until expires_at (never below 0); null without a date. */
export function secondsLeft(expiresAt, now = Date.now()) {
  const t = Date.parse(expiresAt);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((t - now) / 1000));
}

/** 754 -> "12:34"; 3 days -> "3 days"; over an hour -> "2 h 5 min". */
export function countdownText(sec) {
  if (sec == null) return '';
  if (sec >= 2 * 86400) return `${Math.floor(sec / 86400)} days`;
  if (sec >= 3600) return `${Math.floor(sec / 3600)} h ${Math.floor((sec % 3600) / 60)} min`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** A shop-floor time: "2:14 PM" today, "Oct 24 2:14 PM" another day. */
export function clockText(iso, now = new Date()) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return time;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${time}`;
}
